import App from './App.vue';
import { createApp } from 'vue';
import { getRouter } from './router';
import { Tooltip, Dropdown, vTooltip } from 'floating-vue';
import { createPinia } from 'pinia';
import piniaPersistedState from 'pinia-plugin-persistedstate';
import { createVfm } from 'vue-final-modal';
import Vue3Toasity, { type ToastContainerOptions } from 'vue3-toastify';
import { VueQueryPlugin, type VueQueryPluginOptions } from '@tanstack/vue-query';
import * as Sentry from '@sentry/vue';
import { setupDevtools } from './devtools';
import { setupDevtools as setupDevtoolsVueaka } from '@aka/vue-kit';
import { App as CapacitorApp } from '@capacitor/app';
import type { Router } from 'vue-router';
import { ZodError } from 'zod';

export function createApplication() {
    const app = createApp(App);

    // floating vue
    app.component('Tooltip', Tooltip).component('Dropdown', Dropdown).directive('tooltip', vTooltip);

    // pinia
    const pinia = createPinia();
    pinia.use(piniaPersistedState);
    app.use(pinia);

    // router
    const router = getRouter();
    app.use(router);

    // Setup URL open listener for deep linking
    setupDeepLinking(router);

    // vue final modal
    app.use(createVfm());

    // vue3 toastify
    app.use(Vue3Toasity, {
        autoClose: 2000,
    } satisfies ToastContainerOptions);

    // tanstack query
    app.use(VueQueryPlugin, {
        queryClientConfig: {
            defaultOptions: {
                queries: {
                    refetchOnWindowFocus: false,
                    refetchOnMount: true,
                    staleTime: 2 * 60 * 1000, // 2 minutes — cached data serves immediately, refetch in background
                    gcTime: 10 * 60 * 1000, // 10 minutes — keep unused data longer for back-navigation
                    retry(failureCount, error) {
                        if (error instanceof ZodError) {
                            return false;
                        }
                        return failureCount < 3;
                    },
                },
            },
        },
    } satisfies VueQueryPluginOptions);

    // sentry
    if (import.meta.env.VITE_SENTRY_DSN) {
        Sentry.init({
            app,
            dsn: import.meta.env.VITE_SENTRY_DSN,
            integrations: [
                Sentry.browserTracingIntegration(),
                Sentry.replayIntegration(),
                Sentry.feedbackIntegration({
                    showBranding: false,
                }),
            ],
            beforeSend(event) {
                return event;
            },
            tracesSampleRate: 1.0,
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
            release: import.meta.env.VITE_SENTRY_RELEASE,
            environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
            ignoreErrors: [
                'Unable to preload CSS',
                'Request failed with status code 401',
                /Access to.*from script denied/g,
            ],
        });
    }

    // devtools
    app.use({
        install(app) {
            setupDevtools(app);
        },
    });
    app.use({
        install(app) {
            setupDevtoolsVueaka(app);
        },
    });

    return app;
}

function setupDeepLinking(router: Router) {
    console.log('[Deep Linking] Setting up URL open listener');

    CapacitorApp.addListener('appUrlOpen', event => {
        const { url } = event;
        console.log('[Deep Linking] URL opened:', url);

        // Check if the URL is based on VITE_API_URL
        if (url.startsWith(import.meta.env.VITE_API_URL)) {
            console.log('[Deep Linking] Handling API URL redirect:', {
                url,
                apiBaseUrl: import.meta.env.VITE_API_URL,
            });
            // For API URLs, change the window location as it will redirect back to the app
            window.location.href = url;
            return;
        }

        // Check if the URL is based on BASE_URL
        if (url.startsWith(import.meta.env.BASE_URL)) {
            const urlObj = new URL(url);
            const path = urlObj.pathname.replace(import.meta.env.BASE_URL, '');
            console.log('[Deep Linking] Handling internal navigation:', {
                url,
                baseUrl: import.meta.env.BASE_URL,
                path,
                search: urlObj.search,
                hash: urlObj.hash,
            });
            // For internal URLs, use vue-router
            router.push(path + urlObj.search + urlObj.hash);
            return;
        }

        console.warn('[Deep Linking] Unhandled URL scheme:', {
            url,
            apiBaseUrl: import.meta.env.VITE_API_URL,
            baseUrl: import.meta.env.BASE_URL,
        });
    });
}
